import { useMemo, useState } from 'react';
import { Button, ButtonLink } from '../components/primitives';
import {
  getIdeDownloadUrl,
  getIdeGithubReleasesUrl,
  MACOS_IDE_LAUNCH_COMMANDS,
  shouldShowNonMacPlatformNote
} from '../lib/ideDownload';
import {
  AlertIcon,
  AppIconPlaceholder,
  ChipIcon,
  ClipboardIcon,
  CubeIcon,
  DownloadArrowIcon,
  GitHubIcon,
  HelpIcon,
  InfoIcon,
  LaptopIcon,
  ListIcon,
  ShieldIcon
} from './DownloadIcons';

/**
 * macOS download pane — card + alerts (left) and instructions sidebar (right).
 */
export function MacDownloadPane() {
  const downloadUrl = getIdeDownloadUrl();
  const githubReleasesUrl = getIdeGithubReleasesUrl('mac');
  const showPlatformNote = useMemo(
    () => shouldShowNonMacPlatformNote(typeof navigator !== 'undefined' ? navigator.userAgent : ''),
    []
  );
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  async function handleCopyLaunchCommands() {
    try {
      await navigator.clipboard.writeText(MACOS_IDE_LAUNCH_COMMANDS);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 2000);
    } catch {
      setCopyStatus('error');
    }
  }

  return (
    <div className="lp-download-layout">
      <div className="lp-download-main">
        <div className="lp-download-card lp-download-surface">
          <div className="lp-download-card-body">
            <div className="lp-download-card-head">
              <div className="lp-download-card-icon-wrap">
                <AppIconPlaceholder className="lp-download-card-icon" />
              </div>
              <div className="lp-download-card-intro">
                <div className="lp-download-card-title-row">
                  <h2 className="lp-download-card-title">Download for macOS</h2>
                  <span className="lp-download-badge">Unsigned build</span>
                </div>
                <p className="lp-download-card-desc">
                  Early tester build for macOS (Apple Silicon &amp; Intel). Requires macOS 11.0 or
                  later.
                </p>
              </div>
            </div>

            {downloadUrl ? (
              <ButtonLink
                href={downloadUrl}
                variant="primary"
                size="lg"
                className="lp-download-primary-btn"
                data-testid="lp-download"
                rel="noopener noreferrer"
              >
                <DownloadArrowIcon className="lp-download-btn-icon" />
                Download .dmg
              </ButtonLink>
            ) : (
              <p className="lp-download-unavailable" data-testid="lp-download-unavailable">
                Download is not configured yet. Ask your admin to set{' '}
                <code>VITE_IDE_DOWNLOAD_URL</code> on the site build.
              </p>
            )}

            <a
              className="lp-download-github-link"
              href={githubReleasesUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <GitHubIcon className="lp-download-link-icon" />
              View on GitHub Releases →
            </a>
          </div>

          <div className="lp-download-card-foot">
            <div className="lp-download-spec">
              <ChipIcon className="lp-download-spec-icon" />
              <div>
                <span className="lp-download-spec-value">macOS 11+</span>
                <span className="lp-download-spec-label">Minimum</span>
              </div>
            </div>
            <div className="lp-download-spec">
              <LaptopIcon className="lp-download-spec-icon" />
              <div>
                <span className="lp-download-spec-value">Universal</span>
                <span className="lp-download-spec-label">Apple Silicon &amp; Intel</span>
              </div>
            </div>
            <div className="lp-download-spec">
              <CubeIcon className="lp-download-spec-icon" />
              <div>
                <span className="lp-download-spec-value">Unsigned build</span>
                <span className="lp-download-spec-label">For early testers</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lp-download-alert lp-download-surface" data-testid="lp-download-callout">
          <AlertIcon className="lp-download-section-icon" />
          <div>
            <p className="lp-download-section-title">Unsigned build for early testers</p>
            <p className="lp-download-alert-body">
              This build is provided without a code signature and may be flagged by your system.
            </p>
          </div>
        </div>

        {showPlatformNote ? (
          <div className="lp-download-alert lp-download-surface" data-testid="lp-download-platform-note">
            <ShieldIcon className="lp-download-section-icon" />
            <div>
              <p className="lp-download-section-title">Requires macOS</p>
              <p className="lp-download-alert-body">
                Windows installer coming in a future release.
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <aside className="lp-download-sidebar">
        <div className="lp-download-sidebar-card lp-download-surface">
          <div className="lp-download-section-head">
            <ListIcon className="lp-download-section-icon" />
            <h3 className="lp-download-section-title">Instructions</h3>
          </div>
          <ol className="lp-download-sidebar-steps">
            <li>Download the .dmg file using the button above.</li>
            <li>Open the downloaded .dmg file from your Downloads folder.</li>
            <li>Drag Penrose IDE to your Applications folder.</li>
            <li>Launch Penrose IDE from Applications.</li>
          </ol>
        </div>

        <div className="lp-download-sidebar-card lp-download-surface">
          <div className="lp-download-section-head">
            <HelpIcon className="lp-download-section-icon" />
            <h3 className="lp-download-section-title">Troubleshooting</h3>
          </div>
          <p className="lp-download-sidebar-lede">
            If macOS says the app is <strong>damaged</strong>, run:
          </p>
          <div className="lp-download-terminal" data-testid="lp-download-launch-terminal">
            <div className="lp-download-terminal-row">
              <pre className="lp-download-terminal-code">
                <code>{MACOS_IDE_LAUNCH_COMMANDS}</code>
              </pre>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="lp-download-terminal-copy"
                data-testid="lp-download-launch-copy"
                onClick={() => void handleCopyLaunchCommands()}
              >
                <ClipboardIcon className="lp-download-copy-icon" />
                {copyStatus === 'copied'
                  ? 'Copied'
                  : copyStatus === 'error'
                    ? 'Copy failed'
                    : 'Copy'}
              </Button>
            </div>
          </div>
        </div>

        {!downloadUrl ? (
          <div className="lp-download-sidebar-card lp-download-surface">
            <div className="lp-download-section-head">
              <InfoIcon className="lp-download-section-icon" />
              <h3 className="lp-download-section-title">Note</h3>
            </div>
            <p className="lp-download-sidebar-note">
              Download is not configured yet. Ask your admin to set{' '}
              <code>VITE_IDE_DOWNLOAD_URL</code> on the site build.
            </p>
          </div>
        ) : null}
      </aside>
    </div>
  );
}
